<template>
  <div class="flex h-full min-h-0 overflow-hidden">
    <main class="flex flex-1 min-w-0 min-h-0 flex-col overflow-hidden bg-slate-800/40">
      <div class="flex-none px-4 py-3 border-b border-slate-600/70 flex items-center justify-between">
        <div>
          <div class="text-xs font-semibold text-slate-300 uppercase tracking-wide">Arrangement</div>
          <div class="text-xs text-slate-500">All tracks share the same ms scale</div>
        </div>
      </div>

      <div class="flex-1 min-h-0" :class="songViewMode === 'pianoroll' ? 'flex overflow-hidden' : 'overflow-y-auto p-4'">
        <section
          class="min-w-0 bg-slate-700/30"
          :class="songViewMode === 'pianoroll'
            ? 'flex flex-1 min-h-0 flex-col overflow-hidden'
            : 'min-h-full space-y-2 rounded-lg border border-slate-600 p-3'"
        >
          <div class="flex flex-none items-center justify-between border-b border-slate-600/70 p-3 text-xs">
            <div class="flex gap-2">
              <button type="button" class="rounded border px-3 py-1.5 font-medium" :class="songViewMode === 'sequence' ? 'border-cyan-500/40 bg-cyan-500/20 text-cyan-200' : 'border-slate-600 bg-slate-800/60 text-slate-300'" @click="songViewMode = 'sequence'">Sequence</button>
              <button type="button" class="rounded border px-3 py-1.5 font-medium" :class="songViewMode === 'pianoroll' ? 'border-cyan-500/40 bg-cyan-500/20 text-cyan-200' : 'border-slate-600 bg-slate-800/60 text-slate-300'" @click="songViewMode = 'pianoroll'">Piano Roll</button>
            </div>
            <span class="text-slate-500">All enabled tracks · {{ Math.round(playbackTimeMs) }}ms</span>
          </div>
          <div v-if="songViewMode === 'sequence'" class="flex flex-1 min-h-0 flex-col gap-2">
            <SynthSequenceOverview
              :tracks="synth.tracks"
              :playbackTimeMs="playbackTimeMs"
              :viewport="sequenceViewport"
              :bpm="synth.tempo.bpm"
              @seek="$emit('seek', $event)"
              @focus="focusSequence"
              @pan="panSequence"
            />
            <SynthSequenceTimeline
              ref="sequenceTimeline"
              class="flex-1 min-h-0"
              :tracks="synth.tracks"
              :playbackTimeMs="playbackTimeMs"
              :playing="playing"
              :bpm="synth.tempo.bpm"
              showTrackLabels
              showTrackNames
              @viewport-change="sequenceViewport = $event"
            />
          </div>
          <SynthPianoRollPreview
            v-else
            class="flex-1 min-h-0"
            :tracks="enabledTracks"
            :trackIndices="enabledTrackIndices"
            :playbackTimeMs="playbackTimeMs"
            :playing="playing"
            :bpm="synth.tempo.bpm"
            @seek="$emit('seek', $event)"
          />
        </section>
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import SynthPianoRollPreview from './SynthPianoRollPreview.vue'
import SynthSequenceOverview from './SynthSequenceOverview.vue'
import SynthSequenceTimeline from './SynthSequenceTimeline.vue'
import { getSynthPlaybackTrackEntries } from '../../stores/synthPlayback.js'

const props = defineProps({
  synth: { type: Object, required: true },
  composerView: { type: String, default: 'pianoroll' },
  playbackTimeMs: { type: Number, default: 0 },
  playing: { type: Boolean, default: false },
})

const emit = defineEmits(['update:composerView', 'seek'])

const songViewMode = computed({
  get: () => props.composerView,
  set: value => emit('update:composerView', value),
})
const sequenceTimeline = ref(null)
const sequenceViewport = ref({ scrollLeft: 0, viewportWidth: 0, contentWidth: 0 })
const playbackEntries = computed(() => getSynthPlaybackTrackEntries(props.synth, { type: 'song' }))
const enabledTrackIndices = computed(() => playbackEntries.value.map(entry => entry.trackIndex))
const enabledTracks = computed(() => playbackEntries.value.map(entry => entry.track))

watch([songViewMode, () => props.playbackTimeMs], async ([mode]) => {
  if (mode !== 'sequence') return
  await nextTick()
  sequenceTimeline.value?.focusTime?.(props.playbackTimeMs)
})

function focusSequence(timeMs) {
  sequenceTimeline.value?.focusTime?.(timeMs)
}

function panSequence(scrollLeft) {
  sequenceTimeline.value?.setHorizontalScroll?.(scrollLeft)
}
</script>
